import { calculateCompoundCollision, getEffectiveMass } from '@astra/shared';
import type { CollisionCircle } from '@astra/shared';
import { MovableEntity } from './MovableEntity';

/**
 * CollidableEntity - Can collide with other entities.
 *
 * Extends: MovableEntity
 *
 * Provides:
 * - Collision radius and mass for physics
 * - Optional compound collision shapes for ships
 * - Overlap detection
 * - Collision response (delegates to shared/calculateCompoundCollision)
 */
export abstract class CollidableEntity extends MovableEntity {
  abstract readonly collisionRadius: number;
  abstract readonly mass: number;

  /** Compound collision shapes in local space. Empty = use single collisionRadius circle. */
  collisionShapes: CollisionCircle[] = [];

  checkOverlap(other: CollidableEntity): boolean {
    const dx = other.x - this.x;
    const dy = other.y - this.y;
    const distSq = dx * dx + dy * dy;
    const minDist = this.collisionRadius + other.collisionRadius;
    return distSq <= minDist * minDist;
  }

  applyCollision(newVelocity: { x: number; y: number }): void {
    this.velocityX = newVelocity.x;
    this.velocityY = newVelocity.y;
    this.targetVelocityX = newVelocity.x;
    this.targetVelocityY = newVelocity.y;
  }

  getEffectiveMass(): number {
    return getEffectiveMass(this.mass);
  }

  /**
   * Calculate collision result with another entity.
   * Uses compound shapes when available, single circle otherwise.
   * Delegates to shared/calculateCompoundCollision() — single source of truth.
   */
  calculateCollisionWith(
    other: CollidableEntity,
    thisIsAsteroid: boolean = false,
    otherIsAsteroid: boolean = false
  ): { velocityA: { x: number; y: number }; velocityB: { x: number; y: number }; separationA: { x: number; y: number }; separationB: { x: number; y: number }; impactSpeed: number } | null {
    const result = calculateCompoundCollision(
      {
        x: this.x, y: this.y,
        velocityX: this.velocityX, velocityY: this.velocityY,
        collisionRadius: this.collisionRadius, mass: this.mass,
        collisionShapes: this.collisionShapes.length > 0 ? this.collisionShapes : undefined,
        heading: this.rotation,
      },
      {
        x: other.x, y: other.y,
        velocityX: other.velocityX, velocityY: other.velocityY,
        collisionRadius: other.collisionRadius, mass: other.mass,
        collisionShapes: other.collisionShapes.length > 0 ? other.collisionShapes : undefined,
        heading: other.rotation,
      },
      thisIsAsteroid,
      otherIsAsteroid
    );

    if (!result) return null;

    return {
      velocityA: result.newVelocityA,
      velocityB: result.newVelocityB,
      separationA: result.separationA,
      separationB: result.separationB,
      impactSpeed: result.impactSpeed,
    };
  }
}
